import { routerMiddleware } from 'connected-react-router';
import createSagaMiddleware from 'redux-saga';
import { configureStore } from '@reduxjs/toolkit';

import { initAuthChannel } from 'app/lib/auth/authChannel';

import { history } from '../lib/router';
import redirectAfterAuthFromAnotherRegion from './middleware/auth/redirectAfterAuthFromAnotherRegion';
import { setLanguage } from './middleware/language/setLanguage';
import mainLineGridMiddleware from './middleware/mainLineGrid';
import { middleware as persistenceMiddleware, restore } from './persistence';
import getRootReducer from './reducers/client';
import { TRANSFORM_SSR_STATE } from './ssrState';

// ---

/**
 * Create client-side redux store.
 *
 * @param {object} [ssrState] – server-side rendered state
 * @param {{ createHttpApiService: Function<ServiceInstance>, language: SupportedLang, brandName: BrandName }}
 * @return {Store<any>}
 */
export function configureClientStore(
  ssrState = {},
  {
    language,
    brandName,
    createHttpApiService,
    flagsmithState,
  },
) {
  const sagaMiddleware = createSagaMiddleware();

  const initialState = getRootReducer(flagsmithState.flags)(ssrState, {
    type: TRANSFORM_SSR_STATE,
  });

  const store = configureStore({
    reducer: getRootReducer(flagsmithState.flags),
    preloadedState: initialState,
    devTools: true,
    middleware: (getDefaultMiddleware) => (
      getDefaultMiddleware({
        thunk: {
          extraArgument: { language, brandName, createHttpApiService },
        },
        serializableCheck: false,
        immutableCheck: false,
      }).concat(
        persistenceMiddleware(),
        mainLineGridMiddleware(),
        redirectAfterAuthFromAnotherRegion,
        routerMiddleware(history),
        sagaMiddleware,
        setLanguage.middleware,
      )
    ),
  });

  store.restore = () => restore(store);
  store.runSagas = sagaMiddleware.run;
  if (initialState.account.clientInfo?.data?.clientId !== null) {
    store.restore();
  }

  store.dispatch(initAuthChannel());

  return store;
}
